import type { ArcGISServiceJson } from './arcgisTypes';

/**
 * Append `?f=json` (or `&f=json`) to a URL and fetch it.
 * Throws on HTTP errors.
 */
export const fetchServiceJson = async (
  url: string,
): Promise<ArcGISServiceJson> => {
  const separator = url.includes('?') ? '&' : '?';
  const response = await fetch(`${url}${separator}f=json`);
  if (!response.ok) {
    throw new Error(
      `ArcGIS service request failed: ${response.status} ${response.statusText}`,
    );
  }
  return response.json() as Promise<ArcGISServiceJson>;
};

/**
 * Strip a trailing `/` and any trailing layer-id segment so the URL
 * points at the service root (e.g. `/FeatureServer` or `/MapServer`)
 * rather than a specific layer (`/FeatureServer/0`).
 */
export const stripLayerId = (url: string): string =>
  url.replace(/\/+$/, '').replace(/\/\d+$/, '');

// The `/FeatureServer` (or `/MapServer`) segment must terminate at a real
// boundary — a layer id (`/0`), a query string, a fragment, or the end of the
// url — otherwise a typo like `/FeatureServers` would match a bare substring
// check and sail through validation.
export const isFeatureServerUrl = (url: string): boolean =>
  /\/FeatureServer(?=[/?#]|$)/i.test(url);

export const isMapServerUrl = (url: string): boolean =>
  /\/MapServer(?=[/?#]|$)/i.test(url);

/**
 * Resolve the query URL for a given service URL.
 *
 * If the URL already ends in a numeric layer id we leave it unchanged.
 * Otherwise it points at a service root: we fetch the root JSON and use the
 * first id in its `layers` array rather than assuming `/0`. Not every service
 * starts at layer 0 — group/sparse-layer services (e.g. a water network whose
 * first layer is `/2`) have no `/0`, so a hard-coded `/0` would 404. If the
 * lookup fails or the service lists no layers, we fall back to `/0`.
 */
export const resolveLayerUrl = async (url: string): Promise<string> => {
  const trimmed = url.trim().replace(/\/+$/, '');
  // Already ends with a layer id — use it verbatim.
  if (/\/\d+$/.test(trimmed)) {
    return trimmed;
  }

  // Service root — discover the first available layer id.
  try {
    const rootJson = await fetchServiceJson(trimmed);
    const firstLayerId = rootJson.layers?.[0]?.id;
    if (typeof firstLayerId === 'number') {
      return `${trimmed}/${firstLayerId}`;
    }
  } catch {
    // Network/parse failure — fall through to the /0 default below.
  }

  return `${trimmed}/0`;
};
